import { useCallback, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, AlertTriangle, ExternalLink, Eye, Loader2, Mail, Paperclip, PencilLine } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useGmailMailboxAddress } from '@features/shared/store/publicConfigStore';
import { useAuthStore } from '@features/authentication/store/authStore';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { quotationService } from '../services/quotationService';
import { ConfirmDraftActionDialog } from './ConfirmDraftActionDialog';
import {
  EmailExtractionStatus,
  type QuotationListItemDto,
} from '../types/quotation.types';
import { KG_ONLY_NOTE } from '../utils/lineItem';

function formatReceivedAt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function buildGmailUrl(threadId: string | null, mailboxAddress: string | null): string | undefined {
  if (!threadId) {
    // No threadId — fall back to inbox view of the right mailbox if we know it.
    return mailboxAddress
      ? `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(mailboxAddress)}#inbox`
      : 'https://mail.google.com/mail/u/0/#inbox';
  }
  return mailboxAddress
    ? `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(mailboxAddress)}#inbox/${threadId}`
    : `https://mail.google.com/mail/u/0/#inbox/${threadId}`;
}

interface AutoDraftReviewCardProps {
  quotation: QuotationListItemDto;
  /** Called after a successful archive so the parent can refresh its list /
   *  stats. Optional — the SSE Archived event will also splice the row out
   *  wherever the workspace master list is mounted, so unwired parents stay
   *  correct on the next refresh. */
  onArchived?: (id: number) => void;
}

export function AutoDraftReviewCard({ quotation, onArchived }: AutoDraftReviewCardProps) {
  const navigate = useNavigate();
  const mailboxAddress = useGmailMailboxAddress();
  const canArchive = useAuthStore((s) => s.hasPermission(PERMISSIONS.QUOTATIONS_ARCHIVE_QUOTATION));
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const handleStartDrafting = useCallback(() => {
    navigate(`/quotations/workspace?id=${quotation.id}`);
  }, [navigate, quotation.id]);

  // Open the draft in the workspace straight into review mode. The workspace consumes the
  // one-shot ?review=1 marker after it selects the draft — see QuotationWorkspacePage.
  const handleReview = useCallback(() => {
    navigate(`/quotations/workspace?id=${quotation.id}&review=1`);
  }, [navigate, quotation.id]);

  const handleArchiveConfirm = useCallback(async () => {
    setIsArchiving(true);
    try {
      await quotationService.archive(quotation.id);
      toast.success('Quotation archived');
      setArchiveDialogOpen(false);
      onArchived?.(quotation.id);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to archive quotation');
    } finally {
      setIsArchiving(false);
    }
  }, [quotation.id, onArchived]);

  const gmailHref = buildGmailUrl(quotation.sourceEmailGmailThreadId, mailboxAddress);
  const ambiguousBanner = renderBanner(quotation.extractionStatus, quotation.isKgOnly ?? false);

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-2">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 space-y-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">
                  {quotation.sourceEmailFrom ?? 'Unknown sender'}
                </span>
                {quotation.quotationNumber && (
                  <Badge variant="outline" className="text-xs font-normal">
                    {quotation.quotationNumber}
                  </Badge>
                )}
              </div>
              <p
                className="line-clamp-2 text-sm text-muted-foreground"
                title={quotation.sourceEmailSubject ?? undefined}
              >
                {quotation.sourceEmailSubject ?? '(no subject)'}
              </p>
              {quotation.sourceEmailAttachmentCount > 0 && (
                <div className="flex items-center gap-1 pt-0.5 text-xs text-muted-foreground">
                  <Paperclip className="h-3 w-3" />
                  {quotation.sourceEmailAttachmentCount}{' '}
                  {quotation.sourceEmailAttachmentCount === 1 ? 'attachment' : 'attachments'}
                </div>
              )}
            </div>
          </div>
          {quotation.sourceEmailReceivedAt && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {formatReceivedAt(quotation.sourceEmailReceivedAt)}
            </span>
          )}
        </div>

        {ambiguousBanner}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          {gmailHref && (
            <a
              href={gmailHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
            >
              <ExternalLink className="h-4 w-4" />
              Open in Gmail
            </a>
          )}
          <Button variant="outline" onClick={handleReview}>
            <Eye className="mr-1.5 h-4 w-4" />
            Review
          </Button>
          <Button
            onClick={handleStartDrafting}
            title="Open the full compose view — header, cabinet, freight, markup controls"
          >
            <PencilLine className="mr-1.5 h-4 w-4" />
            Open in compose
          </Button>
          {canArchive && (
            <Button
              variant="outline"
              onClick={() => setArchiveDialogOpen(true)}
              disabled={isArchiving}
              className="ml-auto"
            >
              {isArchiving
                ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                : <Archive className="mr-1.5 h-4 w-4" />}
              Archive
            </Button>
          )}
        </div>
      </CardContent>
      <ConfirmDraftActionDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        title="Archive draft"
        description={`Archive ${quotation.quotationNumber}? It will be hidden from active views but recoverable.`}
        confirmLabel="Archive"
        onConfirm={handleArchiveConfirm}
        isLoading={isArchiving}
      />
    </Card>
  );
}

function renderBanner(status: EmailExtractionStatus, isKgOnly: boolean): ReactElement | null {
  // 14ykpqtr22u — every line was in kilograms (typically a "per kg price" inquiry). Cutting lists
  // take pieces only, so there is nothing to quote — say so instead of implying a failure.
  if (isKgOnly && status !== EmailExtractionStatus.Pending) {
    return (
      <Alert>
        <AlertDescription>{KG_ONLY_NOTE}</AlertDescription>
      </Alert>
    );
  }
  switch (status) {
    case EmailExtractionStatus.SkippedAmbiguous:
      return (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Auto-extraction was skipped — this email had multiple attachments, no usable
            source, or a low-confidence classification. Click <strong>Review</strong> to pick
            a source and retry.
          </AlertDescription>
        </Alert>
      );
    case EmailExtractionStatus.Failed:
      return (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Extraction failed — click <strong>Review</strong> and retry.
          </AlertDescription>
        </Alert>
      );
    case EmailExtractionStatus.Pending:
      return (
        <Alert>
          <AlertDescription>Extraction in progress…</AlertDescription>
        </Alert>
      );
    default:
      return null;
  }
}
